<script setup>
import PFBaseModal from '@/components/common/modal/PFBaseModal.vue';
import '@sapudex/web-components/dist/Checkbox.js';
import "@sapudex/web-components/dist/Link.js";
import { useModalProvider } from "@/composables/useModalProvider";
import { openInNewTab } from '@/utils/utils.js';
import { useStore } from "vuex";
import { watch, ref, computed } from "vue";

defineOptions({
  name: 'PEPublishModal',
});

const { openModal, isModalOpen, closeModal } = useModalProvider();
const props = defineProps({
  isShown: {
    type: Boolean,
    default: false,
  },
  submitAction: {
    type: Function,
    default: () => false,
  },
  cancelAction: {
    type: Function,
    default: () => false,
  },
});
const emit = defineEmits(["changeIsShown"]);

const confirm = ref(false);
const isRed = ref(false);

const store = useStore();

const textAreaValue = computed({
  get() {
    return store.state.partnerProfile.publishComment;
  },
  set(value) {
    store.dispatch('partnerProfile/updatePublishComment', value);
  },
});

watch(
  () => props.isShown,
  (newValue) => {
    newValue ? openModal() : closeModal();
  },
  { immediate: true }
);

watch(
  isModalOpen,
  (newValue)=> {emit('changeIsShown', newValue)},
  {immediate:true}
)

watch(confirm, (val) => {
  val ? isRed.value = false : null
});

function clearTextArea() {
  store.dispatch('partnerProfile/updatePublishComment', '');
}

function changeColorToRed() {
  isRed.value = true;
}

function refresh() {
  isRed.value = false;
  confirm.value = false;
}

function clearAndCancel() {
  closeModal();
  clearTextArea();
  props.cancelAction();
  refresh();
}

function submitPublish() {
  if (confirm.value) {
    closeModal();
    props.submitAction();
    refresh();
  } else {
    changeColorToRed();
  }
}

</script>
<template>
  <PFBaseModal v-if="isModalOpen" :modalHeaderText="$t('editor.publish.modal.title')" >
    <template #default>
      <div class="mb-3 -ml-2">
        <div class="mb-5 body-s-size">
          {{ $t('editor.publish.modal.message.pilot') }}
        </div>

        <udex-textarea
          v-model="textAreaValue"
          :label="$t('editor.publish.modal.placeholder')"
          :maxlength="150"
        />   
      </div>
      
      <div class="flex items-start -ml-5"> 
        <div>
          <udex-checkbox
            class="w-11"
            @change="((newValue) => confirm = newValue.target.checked)"
            required
          />
        </div>

        <div class="pt-2.5">
          <span :class="{'!text-red-6' : isRed}" class="body-s-size">
            {{ $t('editor.publish.modal.checkbox.first.line') }}
            <udex-link
              class="!text-blue-7 no-underline" 
              accessibleRole="Link" 
              accessibleName="Partner Terms of Use"
              @click.prevent="openInNewTab('/partner-terms-of-use')" 
              tabindex="-1" 
            >
              {{ $t('partner.terms.of.use') }}
            </udex-link>
          </span>
        </div>
      </div>
    </template>

    <template #buttons>
      <udex-button
        @click="submitPublish"
        size="Large"
        slot="endContent"
        accessibleName="Publish Profile"
      >
        {{ $t('button.publish') }}
      </udex-button>
      <udex-button
        @click="clearAndCancel"
        design="Secondary"
        size="Large"
        slot="endContent"
        accessibleName="Cancel Publish Profile"
      >
        {{$t('button.cancel')}}
      </udex-button>
    </template>
  </PFBaseModal>
</template>

<style lang="less" scoped>
udex-textarea {
  --udex-textarea-height: 118px;
}

udex-link {
  --udex-link-text-decoration: none;
  --udex-link-hover-text-decoration: none;
}

</style>